import React, { useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import { useSecureChat } from "./SecureChatContext";
const messageHandler = require("../../utils/secure/messageHandler");
const socketClient = require("../../api/secureSocket");

const MessageInput = ({
  chatId,
  onMessageSent,
  editingMessage,
  onEditSubmit,
  onEditCancel,
}) => {
  const { currentUser, addMessage, setTypingUser } = useSecureChat();
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120
      )}px`;
    }
  }, []);

  // Handle message change
  const handleMessageChange = useCallback(
    (e) => {
      const value = e.target.value;
      setMessage(value);
      adjustTextareaHeight();

      // Emit typing indicator
      const userId = currentUser?._id;
      if (!userId) {
        console.warn(
          "[INPUT] Typing event suppressed: missing currentUser._id"
        );
      }
      if (value.trim() && !isTyping) {
        setIsTyping(true);
        socketClient.emitTyping(chatId, userId, true);
      } else if (!value.trim() && isTyping) {
        setIsTyping(false);
        socketClient.emitTyping(chatId, userId, false);
      }

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout to stop typing indicator
      if (value.trim() && userId) {
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          socketClient.emitTyping(chatId, userId, false);
        }, 2000);
      }
    },
    [chatId, currentUser?._id, isTyping, adjustTextareaHeight]
  );

  // Send message
  const handleSendMessage = useCallback(async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isSending || !chatId) return;

    try {
      setIsSending(true);

      // Stop typing indicator
      if (isTyping) {
        setIsTyping(false);
        socketClient.emitTyping(chatId, currentUser._id, false);
      }

      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Handle editing vs sending new message
      if (editingMessage) {
        // Edit existing message
        const editedMessage = await messageHandler.editMessage(
          editingMessage._id,
          chatId,
          trimmedMessage
        );

        // Call edit submit callback
        onEditSubmit?.(editedMessage);

        // Emit edited message over socket
        try {
          const senderObject = {
            _id: currentUser._id,
            name: currentUser.name,
            profilePic: currentUser.profilePic,
          };
          socketClient.emitMessageEdited(
            chatId,
            editingMessage._id,
            editedMessage.content,
            editedMessage.iv,
            editedMessage.authTag,
            editedMessage.editedAt,
            senderObject,
            true
          );
        } catch (_e) {}
      } else {
        // Send new message
        const sentMessage = await messageHandler.sendMessage(
          chatId,
          trimmedMessage
        );

        // Add message to context (optimistic render)
        addMessage({
          ...sentMessage,
          decryptedContent: trimmedMessage,
          sender: {
            _id: currentUser._id,
            name: currentUser.name,
            profilePic: currentUser.profilePic,
          },
        });

        // Emit over socket so other participants get it in real time
        try {
          const senderObject =
            sentMessage?.sender &&
            typeof sentMessage.sender === "object" &&
            sentMessage.sender._id
              ? sentMessage.sender
              : {
                  _id: currentUser._id,
                  name: currentUser.name,
                  profilePic: currentUser.profilePic,
                };
          socketClient.emitSecureMessage({
            chat: { _id: chatId },
            content: sentMessage.content,
            iv: sentMessage.iv,
            authTag: sentMessage.authTag,
            sender: senderObject,
            createdAt: sentMessage.createdAt,
            _id: sentMessage._id,
          });
        } catch (_e) {}
      }

      // Clear input
      setMessage("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      // Callback for parent component
      onMessageSent?.();
    } catch (error) {
      console.error("Error sending message:", error);
      // You could add a toast notification here
    } finally {
      setIsSending(false);
    }
  }, [
    message,
    isSending,
    chatId,
    isTyping,
    currentUser,
    addMessage,
    onMessageSent,
    editingMessage,
    onEditSubmit,
  ]);

  // Handle key press
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTyping && currentUser?._id) {
        socketClient.emitTyping(chatId, currentUser._id, false);
      }
    };
  }, [chatId, currentUser?._id, isTyping]);

  // Focus textarea when chat changes
  useEffect(() => {
    if (chatId && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [chatId]);

  // Handle editing mode
  useEffect(() => {
    if (editingMessage) {
      setMessage(editingMessage.decryptedContent || "");
      // Focus and move cursor to end
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const length = message.length;
          textareaRef.current.setSelectionRange(length, length);
        }
      }, 0);
    } else {
      setMessage("");
    }
  }, [editingMessage]);

  const canSend = message.trim().length > 0 && !isSending;

  // Handle cancel editing
  const handleCancelEdit = () => {
    setMessage("");
    onEditCancel?.();
  };

  return (
    <Container>
      {editingMessage && (
        <EditModeIndicator>
          <EditModeText>Editing message</EditModeText>
          <CancelButton onClick={handleCancelEdit}>Cancel</CancelButton>
        </EditModeIndicator>
      )}
      <InputContainer>
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          placeholder={editingMessage ? "Edit message..." : "Type a message..."}
          disabled={isSending}
          rows={1}
        />

        <SendButton
          onClick={handleSendMessage}
          disabled={!canSend}
          $isSending={isSending}
        >
          {isSending ? (
            <LoadingSpinner />
          ) : (
            <SendIcon>{editingMessage ? "✓" : "→"}</SendIcon>
          )}
        </SendButton>
      </InputContainer>
    </Container>
  );
};

export default MessageInput;

// Styled Components
const Container = styled.div`
  padding: 16px 20px;
  background-color: var(--color-surface);
  border-top: 1px solid var(--color-border);
`;

const EditModeIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
`;

const EditModeText = styled.span`
  font-size: 12px;
  color: var(--color-primary);
  font-weight: 500;
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--color-surface);
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 24px;
  padding: 8px 12px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
  }
`;

const Textarea = styled.textarea`
  flex: 1;
  border: none;
  background: none;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 1.4;
  color: var(--color-text-tertiary); /* Changed to text-tertiary */
  min-height: 36px;
  padding: 8px 0;
  max-height: 120px;
  font-family: inherit;

  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: ${(props) => (props.disabled ? "var(--color-border)" : "var(--color-text)")};
  color: var(--color-surface);
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: var(--color-primary);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const SendIcon = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
